import {
  Dialog,
  Transition,
  TransitionChild,
  DialogPanel,
} from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/20/solid'
import React, { Fragment, PropsWithChildren } from 'react'
import { rouna, rounaDisplay, rounaMono } from 'utils/fonts'
import { useRouter } from 'next/router'
import Link from 'next/link'
import { useOrdersCount } from 'hooks/useOrdersCount'
import DiscordIcon from '@components/icons/DiscordIcon'
import { XIcon } from '@components/icons/XIcon'
import GlowBadge from '@components/shared/GlowBadge'
import SeasonEndedBadge from '@components/shared/SeasonEndedBadge'
import { DISCORD_LINK, TELEGRAM_LINK, TWITTER_LINK } from 'utils/constants'
import { useTranslation } from 'next-i18next'
import { ExternalLink } from '@components/shared/ExternalLink'
import { clsx } from 'clsx'
import TelegramIcon from '@components/icons/TelegramIcon'

const MobileNav = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) => {
  const { t } = useTranslation('mobile-nav')
  const { asPath } = useRouter()
  const { ordersCount } = useOrdersCount()

  return (
    <Transition show={isOpen}>
      <Dialog className="fixed inset-0 z-30" onClose={onClose}>
        <TransitionChild
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          as={Fragment}
        >
          <div className="fixed inset-0 z-40 cursor-default bg-black/50" />
        </TransitionChild>
        <TransitionChild
          enter="transition ease-in duration-300"
          enterFrom="-translate-x-full"
          enterTo="translate-x-0"
          leave="transition ease-out duration-300"
          leaveFrom="translate-x-0"
          leaveTo="-translate-x-full"
          as={Fragment}
        >
          <DialogPanel
            className={`thin-scroll absolute left-0 z-40 size-full overflow-y-auto bg-th-bkg-1 text-left font-body md:w-96 ${rounaDisplay.variable} ${rounaMono.variable} ${rouna.variable}`}
          >
            <div className="flex justify-end p-2">
              <button
                onClick={onClose}
                className="flex size-8 items-center justify-center rounded-full text-th-fgd-3 focus-visible:bg-th-bkg-3 md:hover:bg-th-bkg-2"
              >
                <XMarkIcon className="size-5" />
              </button>
            </div>
            <div className="flex h-[calc(100%-80px)] flex-col justify-between px-6">
              <div className="space-y-3">
                <MobileNavLink
                  href="/"
                  isSelected={() => asPath.includes('/swap/') || asPath === '/'}
                  onClick={onClose}
                >
                  {t('swap')}
                </MobileNavLink>
                <MobileNavLink
                  href="/trending"
                  currentPath={asPath}
                  onClick={onClose}
                >
                  {t('trending')}
                </MobileNavLink>
                <MobileNavLink
                  href="/orders"
                  currentPath={asPath}
                  onClick={onClose}
                  className="relative"
                >
                  {t('orders')}
                  {ordersCount ? (
                    <div className="absolute -top-2.5 left-14 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-th-active px-1">
                      <span className="font-mono text-[10px] font-semibold text-th-button">
                        {ordersCount}
                      </span>
                    </div>
                  ) : null}
                </MobileNavLink>
                <MobileNavLink
                  href="/leaderboard"
                  currentPath={asPath}
                  onClick={onClose}
                  className="relative inline-block"
                >
                  {t('leaderboard')}
                  <SeasonEndedBadge
                    text={t('season-1-ended')}
                    className="pointer-events-none absolute -right-8 -top-2 rotate-6 shadow-sm"
                  />
                </MobileNavLink>
                {/* HIDDEN_PROFILES_REFERRALS: revert via PROFILES_AND_REFERRALS_HIDDEN.md
                <MobileNavLink
                  href="/referrals"
                  currentPath={asPath}
                  onClick={onClose}
                >
                  {t('referrals')}
                </MobileNavLink>
                */}
                <MobileNavLink
                  href="/vaults"
                  currentPath={asPath}
                  onClick={onClose}
                >
                  {t('vaults')}
                </MobileNavLink>
                <MobileNavLink
                  href="/bluechip"
                  currentPath={asPath}
                  onClick={onClose}
                >
                  {t('revenue')}
                </MobileNavLink>
                {/* <MobileNavLinkWithBadge
                  href="/swap-contest"
                  badgeText={t('badges.live')}
                  currentPath={asPath}
                  onClick={onClose}
                >
                  {t('contest')}
                </MobileNavLinkWithBadge> */}
              </div>
              <div className="flex items-center justify-center gap-4">
                <ExternalLink
                  className="flex items-center gap-2"
                  href={DISCORD_LINK}
                >
                  <DiscordIcon className="size-5" />
                  <span className="text-th-fgd-3">
                    {t('social.join-discord')}
                  </span>
                </ExternalLink>
                <span className="text-th-fgd-4">|</span>
                <ExternalLink
                  className="flex items-center gap-2"
                  href={TWITTER_LINK}
                >
                  <XIcon className="size-5" />
                  <span className="text-th-fgd-3">{t('social.follow-us')}</span>
                </ExternalLink>
                <ExternalLink
                  className="flex items-center gap-2"
                  href={TELEGRAM_LINK}
                >
                  <TelegramIcon className="size-5" />
                  <span className="text-th-fgd-3">Telegram</span>
                </ExternalLink>
              </div>
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  )
}

export default MobileNav

type MobileNavLinkProps = PropsWithChildren<
  {
    href: string
    onClick?: () => void
    className?: string
    disabled?: boolean
  } & ({ currentPath: string } | { isSelected: () => boolean })
>

const MobileNavLink: React.FC<MobileNavLinkProps> = (props) => {
  const { href, onClick, children, className, disabled } = props
  return (
    <Link
      href={disabled ? '/' : href}
      onClick={disabled ? (e) => e.preventDefault() : onClick}
      className={clsx(
        className,
        'block text-base',
        ('isSelected' in props && props.isSelected()) ||
          ('currentPath' in props && props.currentPath === href)
          ? 'text-th-button'
          : 'text-th-fgd-2',
        disabled && 'cursor-not-allowed',
      )}
    >
      {children}
    </Link>
  )
}

const MobileNavLinkWithBadge: React.FC<
  MobileNavLinkProps & { badgeText: string }
> = ({ badgeText, className, children, ...otherProps }) => (
  <MobileNavLink
    {...otherProps}
    className={clsx(className, 'flex items-center gap-2')}
  >
    <div>{children}</div>
    <GlowBadge text={badgeText} />
  </MobileNavLink>
)
